import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const Process = () => {
  const [tabValue, setTabValue] = useState(0);

  /* ================= PROCESS TAB STATES ================= */
  const [processName, setProcessName] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [snackbar, setSnackbar] = useState(false);
  const [status, setStatus] = useState("");
  const [processId, setProcessId] = useState("");
  const [parameter, setParameter] = useState("");
  const [executionDetails, setExecutionDetails] = useState(null);

  const processes = [
    { id: 1, name: "Import Collections", code: "PRC_1001" },
    { id: 2, name: "Reconciliation", code: "PRC_1002" }
  ];
  const parameters = [
    { id: 1, name: "CRNS - 1" },
    { id: 2, name: "CRNS - 2" },
    { id: 3, name: "FULL LOAD" }
  ];


  const getStatusChip = (status) => {
    const colors = {
      RUNNING: "primary",
      PENDING: "info",
      COMPLETED: "success",
      FAILED: "error"
    };
    return <Chip label={status} color={colors[status]} size="small" />;
  };

  const processColumns = [
    { field: "jobId", headerName: "Job Id", flex: 1 },
    { field: "processName", headerName: "Program Name", flex: 1 },
    { field: "date", headerName: "Date", flex: 1 },
    { field: "user", headerName: "User", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => getStatusChip(params.value)
    }
  ];

  const handleRunNow = () => {
    if (!processName) return;

    const newJob = {
      id: Date.now(),
      processName,
      jobId: `${processName}_${Date.now()}`,
      date: new Date().toLocaleString(),
      status: "PENDING",
      parameter,
      user: "Admin"
    };

    setJobs((prev) => [newJob, ...prev]);
    setSnackbar(true);

    // 👇 Set execution panel details
    setExecutionDetails(newJob);

    setIsRunning(true);

    setTimeout(() => {
      setJobs((prev) =>
        prev.map((job) =>
          job.id === newJob.id ? { ...job, status: "COMPLETED" } : job
        )
      );

      // 👇 Update execution panel status too
      setExecutionDetails((prev) =>
        prev ? { ...prev, status: "COMPLETED" } : prev
      );

      setIsRunning(false);
    }, 4000);
  };

  const handleSchedule = () => {
    if (!scheduleDate) return;

    const newJob = {
      id: Date.now(),
      processName,
      jobId: `${processName}_SCH_${Date.now()}`,
      date: new Date(scheduleDate).toLocaleString(),
      status: "PENDING",
      parameter,
      user: "Admin"
    };

    setJobs((prev) => [newJob, ...prev]);
    setScheduleOpen(false);
    setSnackbar(true);

    // 👇 Update execution panel
    setExecutionDetails(newJob);
  };

  /* ================= SEARCH TAB STATES ================= */

  const [jobId, setJobId] = useState("");
  const [jobName, setJobName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchRows, setSearchRows] = useState([]);

  const jobData = [
    {
      id: 1,
      jobId: "JOB_1001",
      jobName: "Import Collections",
      startDate: "2026-02-20",
      endDate: "2026-02-20",
      status: "COMPLETED"
    },
    {
      id: 2,
      jobId: "JOB_1002",
      jobName: "Reconciliation",
      startDate: "2026-02-21",
      endDate: "2026-02-21",
      status: "RUNNING"
    }
  ];

  const searchColumns = [
    { field: "jobId", headerName: "Job ID", flex: 1 },
    { field: "jobName", headerName: "Job Name", flex: 1 },
    { field: "startDate", headerName: "Start Date", flex: 1 },
    { field: "endDate", headerName: "End Date", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => getStatusChip(params.value)
    }
  ];

  const handleSearch = () => {
    const filtered = jobData.filter((job) => {
      return (
        (jobId === "" || job.jobId.toLowerCase().includes(jobId.toLowerCase())) &&
        (jobName === "" || job.jobName.toLowerCase().includes(jobName.toLowerCase())) &&
        (startDate === "" || job.startDate >= startDate) &&
        (endDate === "" || job.endDate <= endDate)
      );
    });
    setSearchRows(filtered);
  };

  const handleReset = () => {
    setJobId("");
    setJobName("");
    setStartDate("");
    setEndDate("");
    setSearchRows([]);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
        Process Manager
      </Typography>

      {/* ================= TABS ================= */}
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Process" />
        <Tab label="Search Process" />
      </Tabs>

      {/* ================= PROCESS TAB ================= */}
      {tabValue === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">

              <Grid item xs={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Program Name"
                  value={processName}
                  onChange={(e) => setProcessName(e.target.value)}
                >
                  {processes.map((p) => (
                    <MenuItem key={p.id} value={p.name}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Parameter"
                  value={parameter}
                  onChange={(e) => setParameter(e.target.value)}
                >
                  {parameters.map((p) => (
                    <MenuItem key={p.id} value={p.name}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ✅ Buttons Section */}
              <Grid item xs>
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleRunNow}
                      disabled={!processName || isRunning}
                      sx={{
                        backgroundColor: "#1976d2",
                        "&:hover": {
                          backgroundColor: "#1565c0"
                        }
                      }}
                    >
                      Run Now
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<ScheduleIcon />}
                      onClick={() => setScheduleOpen(true)}
                      disabled={!processName}
                      sx={{
                        backgroundColor: "#1976d2",
                        "&:hover": {
                          backgroundColor: "#1565c0"
                        }
                      }}
                    >
                      Schedule
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          </Paper>

          {/* -------------------- EXECUTION PANEL -------------------- */}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6">Process Execution</Typography>

            <Typography>
              Name: {executionDetails?.processName}
            </Typography>

            <Typography>
              Job ID: {executionDetails?.jobId}
            </Typography>

            <Typography>
              Date: {executionDetails?.date}
            </Typography>

            <Typography>
              Parameter: {executionDetails?.parameter}
            </Typography>

            {executionDetails?.status && (
              <Box mt={1}>
                {getStatusChip(executionDetails.status)}
              </Box>
            )}
          </Paper>


          <Paper sx={{ height: 400, p: 2 }}>
            <Typography variant="h6" mb={2}>
              Execution History
            </Typography>
            <DataGrid rows={jobs} columns={processColumns} />
          </Paper>
        </>
      )}

      {/* ================= SEARCH TAB ================= */}
      {tabValue === 1 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">

              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Job ID"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                />
              </Grid>

              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Job Name"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                />
              </Grid>

              <Grid item xs={1.5}>
                <TextField
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={1.5}>
                <TextField
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="RUNNING">RUNNING</MenuItem>
                  <MenuItem value="PENDING">PENDING</MenuItem>
                  <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                  <MenuItem value="FAILED">FAILED</MenuItem>
                </TextField>
              </Grid>

              {/* ✅ Buttons aligned right */}
              <Grid item xs>
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      sx={{
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#1565c0" }
                      }}
                    >
                      Search
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<RestartAltIcon />}
                      onClick={handleReset}
                      sx={{
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#1565c0" }
                      }}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          </Paper>

          <Paper sx={{ height: 400, p: 2 }}>
            <Typography variant="h6" mb={2}>
              Search Results
            </Typography>
            <DataGrid rows={searchRows} columns={searchColumns} />
          </Paper>
        </>
      )}

      {/* ================= MODAL ================= */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)}>
        <DialogTitle>Schedule Process</DialogTitle>
        <DialogContent>
          <TextField
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            onChange={(e) => setScheduleDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSchedule}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(false)}
      >
        <Alert severity="success" variant="filled">
          Job Submitted Successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Process;